// ─── Image generation ──────────────────────────────────────────────────────
//
// Everything goes through our own /api/images/edit endpoint, which calls
// Pollinations (Grok Imagine, with nanobanana/seedream behind it) using a key
// that stays on the server. Anything in a REACT_APP_ variable ships inside the
// public bundle, where anyone can read it and spend the account's balance.
//
// There is deliberately no in-browser provider left here. The old FAL fallback
// both leaked its key into the bundle and stopped working when that account ran
// out of balance, so a failure had nowhere to go. Fallback now lives server-side
// as a chain of models, because these providers decide moderation per model: the
// same photo and prompt that xAI refuses is accepted by nanobanana and seedream.

const GENERATE_TIMEOUT_MS = 240000;

function fetchWithTimeout(url, options = {}, timeoutMs = 30000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...options, signal: controller.signal })
    .finally(() => clearTimeout(timer));
}

// ─── Prompt shaping ────────────────────────────────────────────────────────
// Tuned against real booth output — the people-count and identity rules are the
// ones image models violate most often.
const PROMPT_KEYWORDS = [
  'CRITICAL: this is a PHOTO EDIT — keep each person\'s original face, gender, age, and skin tone EXACTLY',
  // PEOPLE COUNT — most violated rule. Repeat aggressively.
  'COUNT the people in the input photo. Your output MUST contain the EXACT same number of people — not one more, not one less',
  'DO NOT add extra people, DO NOT add classmates, DO NOT add friends, DO NOT add bystanders, DO NOT add background characters',
  'DO NOT add any person who is not visible in the original input photo. ZERO new people allowed',
  'if the input has 3 people, the output has exactly 3. If 4, exactly 4. If 5, exactly 5. The count is INVIOLABLE',
  // Identity preservation
  'each person keeps their OWN gender — a girl stays a girl, a boy stays a boy, never swap genders',
  'each person keeps their OWN skin tone — do not lighten, do not darken, do not change ethnicity of any individual',
  'keep the EXACT same faces from the original — same eyes, same nose, same mouth, same hairstyle, same hair color',
  'do NOT generate new faces, do NOT invent faces, do NOT replace anyone with a stock face',
  // Quality
  'maintain ultra-sharp, high-resolution faces with smooth realistic skin texture',
  'NO noise, NO grain, NO compression artifacts, NO dotted texture on faces',
  'NO face distortion, NO morphing, NO blending of faces',
  'photorealistic faces, crisp clear eyes, smooth skin, professional DSLR portrait quality, 8k resolution',
  // Composition
  'subjects PERFECTLY CENTERED in the frame and FILLING the composition',
  'tight composition — faces in the upper-center of the image, body framed from waist or knees up',
  'NO empty headroom above the people, NO empty floor below — subjects fill the frame edge to edge',
  'portrait framing like a professional studio shot, eyes positioned at ~40% from the top of the frame',
];

function enhancePrompt(prompt) {
  return PROMPT_KEYWORDS.reduce(
    (p, kw) => p.toLowerCase().includes(kw.slice(0, 15).toLowerCase()) ? p : `${p}, ${kw}`,
    prompt
  );
}

// `size` and `resolution` are separate knobs on Grok Imagine and easy to
// confuse: `size` only sets the ASPECT RATIO (the model snaps to its own nearest
// bucket), while `resolution` picks the 1K/2K output tier. Passing size='2k' is
// parsed as an aspect ratio and quietly returns a 9:16 image.
function resolveAspectForFrame(frameId) {
  if (frameId === 'polaroid_portrait')  return '1600x2000'; // 4:5  → model returns ~3:4
  if (frameId === 'polaroid_landscape') return '1920x1008'; // ~17:9 → model returns 16:9
  return null; // no event frame — let the model follow the source photo's shape
}

/** Error carrying enough detail for the UI to explain itself to a guest. */
export class GenerationError extends Error {
  constructor(message, { reason = null, status = 0 } = {}) {
    super(message);
    this.name = 'GenerationError';
    this.reason = reason;
    this.status = status;
  }
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Could not read the image'));
    reader.readAsDataURL(blob);
  });
}

function compressImage(file, maxSize = 1536) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        const MIN = 256;
        if (width < MIN || height < MIN) {
          const s = Math.max(MIN / width, MIN / height);
          width = Math.ceil(width * s); height = Math.ceil(height * s);
        } else if (width > maxSize || height > maxSize) {
          const s = Math.min(maxSize / width, maxSize / height);
          width = Math.floor(width * s); height = Math.floor(height * s);
        }
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => resolve(new File([blob], file.name || 'photo.jpg', { type: 'image/jpeg', lastModified: Date.now() })),
          'image/jpeg', 0.9
        );
      };
      img.onerror = () => resolve(file);
    };
    reader.onerror = () => resolve(file);
  });
}

// ─── Public API ────────────────────────────────────────────────────────────

export async function generateCharacterImage({
  prompt,
  promptNeutral = null,
  imageFile,
  quality = 'standard',
  faceCount = 1,
  frameId = null,
  getAuthToken = null,
}) {
  if (!imageFile) throw new GenerationError('No photo to work from');

  const groupLabel = faceCount > 1 ? `group of ${faceCount}` : 'single person';
  console.log(`🚀 Starting generation [${groupLabel}]${frameId ? ` · frame: ${frameId}` : ''}`);

  // The reference drives identity, not output resolution, so a 1536px cap keeps
  // facial detail while keeping the upload small enough for a venue's uplink.
  const compressed = await compressImage(imageFile, 1536);
  const dataUrl = await blobToDataUrl(compressed);

  const headers = { 'Content-Type': 'application/json' };
  if (getAuthToken) {
    const token = await getAuthToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const isUltra = quality === 'ultra' || quality === 'hd' || quality === '4k';
  const body = {
    prompt: enhancePrompt(prompt),
    dataUrl,
    // Grok Imagine accepts only 'low' or 'medium' — 'high' is an HTTP 400.
    quality: 'medium',
    resolution: isUltra ? '2k' : '1k',
  };
  // Sent unenhanced on purpose: this is the wording the server retries the
  // preferred model with when it refuses the job, so piling the emphatic
  // keywords back on would defeat the point.
  if (promptNeutral) body.promptNeutral = promptNeutral;
  const size = resolveAspectForFrame(frameId);
  if (size) body.size = size;

  console.log(`🌐 [Pollinations] ${body.resolution}${size ? ` · ${size}` : ''}`);
  const t0 = Date.now();

  let response;
  try {
    response = await fetchWithTimeout(
      '/api/images/edit',
      { method: 'POST', headers, body: JSON.stringify(body) },
      GENERATE_TIMEOUT_MS
    );
  } catch (err) {
    if (err.name === 'AbortError') throw new GenerationError('Generation timed out', { reason: 'timeout' });
    throw new GenerationError(err.message, { reason: 'network' });
  }

  if (!response.ok) {
    let payload = {};
    try { payload = await response.json(); } catch { /* non-JSON body */ }
    throw new GenerationError(payload.error || `HTTP ${response.status}`, {
      reason: payload.reason || (response.status === 401 ? 'auth' : null),
      status: response.status,
    });
  }

  const { dataUrl: outputUrl, model, attempt, moderationFallback, degraded, usage } = await response.json();
  if (!outputUrl) throw new GenerationError('No image was returned', { reason: 'empty' });

  console.log(`✅ ${attempt || model} done in ${((Date.now() - t0) / 1000).toFixed(1)}s${moderationFallback ? ' (fallback model)' : ''}`);
  return {
    // A data: URL, not a blob: — printable and persistable outside this tab.
    imageUrl: outputUrl,
    provider: `pollinations-${model}`,
    raw: { model, attempt, moderationFallback, degraded, usage },
    degraded: !!degraded,
  };
}

export async function getProviderStatus() {
  try {
    const res = await fetchWithTimeout('/api/health', {}, 8000);
    return { pollinations: { available: res.ok, error: res.ok ? null : `HTTP ${res.status}` } };
  } catch (e) {
    return { pollinations: { available: false, error: e.message } };
  }
}
